<script setup lang="ts">
// this component should always be at the root of the domain

// preload the map tiles, so they are in the cache for loading in subsequent views
import mapboxgl from 'mapbox-gl'

import '@/assets/app.css'
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import 'mapbox-gl/dist/mapbox-gl.css'

import FlashBanners from '@/components/banners/FlashBanners.vue'
import ParticipantLinkBanner from '@/components/banners/ParticipantLinkBanner.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import InvalidVersionModal from '@/components/modals/InvalidVersionModal.vue'
import UIFloatingToolTip from '@/components/ui/UIFloatingToolTip.vue'
import { provideFullDataLayers } from '@/composables/data/useFullDataLayers'
import { provideFullEffects } from '@/composables/data/useFullEffects'
import { provideFullRemedies } from '@/composables/data/useFullRemedies'
import { useAnalytics } from '@/composables/useAnalytics'
import { getEnv } from '@/composables/useEnv'
import { loadIntercom } from '@/composables/useIntercom'
import { useIntercomActions } from '@/composables/useIntercomActions'
import { initLanguage } from '@/composables/useLanguageSetters'
import {
  baseMapStyleUrls,
  defaultBaseMap,
  getDefaultMapOptions
} from '@/config/map'
import { useConfirmStore } from '@/store/confirm'
import { useModalStore } from '@/store/modal'
import { useTutorialStore } from '@/store/tutorial'
import { useUserStore } from '@/store/user'
import { hasWebGL } from '@/utils/webgl'
import { IFeatureFlags, TutorialEventAction } from '@lifk/types'
import { storeToRefs } from 'pinia'
import { onMounted, provide, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()
const userStore = useUserStore()
const confirmStore = useConfirmStore()
const modalStore = useModalStore()
const tutorialStore = useTutorialStore()
const { isActive, celebrating } = storeToRefs(tutorialStore)
const { trackTutorial } = useIntercomActions()

let tutorialCompleted = false

const reportTutorial = (action: TutorialEventAction) => {
  if (userStore.user?.role === 'guest') return
  trackTutorial(action, tutorialStore.activeTutorialId)
}

watch(isActive, (active, wasActive) => {
  if (active && !wasActive) {
    tutorialCompleted = false
    reportTutorial('started')
  } else if (!active && wasActive && !tutorialCompleted) {
    reportTutorial('dismissed')
  }
})

watch(celebrating, (isCelebrating) => {
  if (!isCelebrating) return
  tutorialCompleted = true
  reportTutorial('completed')
})

const environment = getEnv('VITE_APP_ENV') // grabs app environment
const { identifyUser } = useAnalytics()

/*
  Init and provide app-level state
*/
initLanguage() // grabs language from participant link querystring, so it is automatically set in participant link
const { currentRemedies } = provideFullRemedies()
provideFullEffects(currentRemedies)
provideFullDataLayers()

// Feature flags can be set using environment variables
const featureFlags = ref<IFeatureFlags>({
  comments: true,
  settings: getEnv('VITE_APP_SETTINGS') === 'true'
})
provide('featureFlags', featureFlags)

if (environment !== 'production') {
  document.title = `[${environment.toUpperCase()}] ${document.title}`
}

onMounted(async () => {
  // disable intercom for iframes
  if (!route.name?.toString().startsWith('embed-map')) {
    // Load intercom and identify user if they are not a guest
    loadIntercom()
  }

  if (userStore.user && userStore.user.role !== 'guest') {
    identifyUser(userStore.user)
  }

  // on mounted - preload map
  if (!hasWebGL()) return // skip if no webgl
  mapboxgl.accessToken = getEnv('VITE_APP_MAPBOX_TOKEN')
  new mapboxgl.Map({
    container: 'mapbox-preload',
    style: baseMapStyleUrls[defaultBaseMap],
    ...getDefaultMapOptions()
  })
})
</script>

<template>
  <RouterView name="root" class="absolute inset-0" />
  <!-- across-view modals -->
  <ConfirmModal
    v-if="confirmStore.isOpen"
    class="z-[60]"
    @confirm="confirmStore.confirm()"
    @cancel="confirmStore.cancel()"
  />
  <RouterView name="modal" class="absolute inset-0 z-20" />
  <RouterView name="privacyModal" class="absolute inset-0 z-20" />
  <InvalidVersionModal v-if="modalStore.invalidVersionModalShowing" />
  <RouterView name="contributorModal" />
  <!-- across-view UI -->
  <RouterView name="collaborateBanner" />
  <ParticipantLinkBanner />
  <FlashBanners />
  <UIFloatingToolTip />
  <div
    id="mapbox-preload"
    style="position: absolute; top: -100vh; left: 0; width: 100%; height: 100%"
  ></div>
</template>
